import type { CompanionDebugRound } from '@edge/edge-ai'

/**
 * Channel name for the AI/LLM profiling entries in the dev debug sidebar. All
 * AI flows (companion now; analyst/research later) emit here so the panel has
 * one place to profile cost + latency per round.
 */
export const AI_DEBUG_CHANNEL = 'ai'

/** Running conversation totals carried on each round so the per-entry view can
 *  show a session rollup without the sidebar needing cross-entry access. */
export interface LlmSessionTotals {
  rounds: number
  modelMs: number
  toolMs: number
  tokensIn?: number
  tokensOut?: number
  costUsd?: number
}

/**
 * Generic per-round LLM profiling envelope rendered by the `ai` debug view.
 * Feature-agnostic on purpose: companion maps into it today, analyst/research
 * map into it in later phases. Token/cost fields stay optional until the
 * backend emits a terminal usage event (Phase B onward).
 */
export interface LlmCallMeta {
  feature: string
  /** Stable per-manager/run id; the sidebar tab key. */
  conversationId: string
  /** Groups the rounds of one user request/turn (the tool loop). */
  correlationId: string
  round: number
  modelRequested?: string
  modelServed?: string
  modelsAttempted?: string[]
  /** Time to first token (ms). The dominant perceived-latency signal. */
  ttftMs?: number
  /** Wall time of this model round (ms): the model-time half of the split. */
  totalMs: number
  /** Tool-execution time this round triggered (ms): the tool-time half. */
  toolMs?: number
  tokensIn?: number
  tokensOut?: number
  cachedTokens?: number
  costUsd?: number
  toolCalls?: { name: string }[]
  input?: unknown
  output?: unknown
  ok: boolean
  error?: string
  /** Conversation totals up to and including this round. */
  session?: LlmSessionTotals
}

/** Map a package-level companion round into the generic app envelope. */
export function companionRoundToMeta(
  round: CompanionDebugRound,
  session: LlmSessionTotals,
): LlmCallMeta {
  return {
    feature: round.feature,
    conversationId: round.conversationId,
    correlationId: round.turnId,
    round: round.round,
    modelRequested: round.modelRequested,
    modelServed: round.modelServed,
    ttftMs: round.ttftMs,
    totalMs: round.totalMs,
    toolMs: round.toolMs,
    tokensIn: round.tokensIn,
    tokensOut: round.tokensOut,
    cachedTokens: round.cachedTokens,
    costUsd: round.costUsd,
    toolCalls: round.toolCalls,
    input: round.input,
    output: round.output,
    ok: round.ok,
    error: round.error,
    session,
  }
}

/** Output tokens per second over the round's wall time. Undefined until the
 *  backend supplies token counts (Phase B). */
export function tokensPerSecond(meta: LlmCallMeta): number | undefined {
  if (meta.tokensOut == null || !meta.totalMs) return undefined
  return meta.tokensOut / (meta.totalMs / 1000)
}
